/**
 * Deploy-aware client version checks.
 *
 * The running bundle embeds __CLIENT_BUILD_ID__ at build time. Production also
 * serves /version.json with the same id (no-cache, never precached). When they
 * diverge, the installed PWA is stale and must drop caches before reload.
 */

declare const __CLIENT_BUILD_ID__: string;

export const CLIENT_BUILD_ID: string =
  typeof __CLIENT_BUILD_ID__ === "string" && __CLIENT_BUILD_ID__.length > 0
    ? __CLIENT_BUILD_ID__
    : "dev";

export const CLIENT_VERSION_URL = "/version.json";

/** How often to poll while the tab is visible. */
export const CLIENT_VERSION_POLL_MS = 60_000;

export type ClientVersionPayload = {
  buildId: string;
};

export function parseClientVersionPayload(data: unknown): string | null {
  if (!data || typeof data !== "object") return null;
  const buildId = (data as { buildId?: unknown }).buildId;
  if (typeof buildId !== "string") return null;
  const trimmed = buildId.trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function isClientUpdateAvailable(
  runningBuildId: string,
  remoteBuildId: string | null,
): boolean {
  if (!remoteBuildId) return false;
  return remoteBuildId !== runningBuildId;
}

export async function fetchRemoteClientBuildId(
  fetchImpl: typeof fetch = fetch,
): Promise<string | null> {
  const url = `${CLIENT_VERSION_URL}?t=${Date.now()}`;
  const response = await fetchImpl(url, {
    cache: "no-store",
    headers: { accept: "application/json" },
  });
  if (!response.ok) return null;
  const data: unknown = await response.json();
  return parseClientVersionPayload(data);
}

export async function checkForClientUpdate(
  fetchImpl: typeof fetch = fetch,
  runningBuildId: string = CLIENT_BUILD_ID,
): Promise<boolean> {
  try {
    const remote = await fetchRemoteClientBuildId(fetchImpl);
    return isClientUpdateAvailable(runningBuildId, remote);
  } catch {
    return false;
  }
}

export async function invalidateClientCaches(
  cachesImpl: CacheStorage | undefined = globalThis.caches,
  serviceWorker: ServiceWorkerContainer | undefined = navigator.serviceWorker,
): Promise<void> {
  if (cachesImpl) {
    const keys = await cachesImpl.keys();
    await Promise.all(keys.map((key) => cachesImpl.delete(key)));
  }
  if (serviceWorker?.getRegistrations) {
    const regs = await serviceWorker.getRegistrations();
    await Promise.all(regs.map((reg) => reg.unregister()));
  }
}

export async function invalidateClientAndReload(
  reload: () => void = () => {
    const url = new URL(window.location.href);
    url.searchParams.set("_v", CLIENT_BUILD_ID);
    window.location.replace(url.toString());
  },
  cachesImpl?: CacheStorage,
  serviceWorker?: ServiceWorkerContainer,
): Promise<void> {
  await invalidateClientCaches(cachesImpl, serviceWorker);
  reload();
}
